/**
 * PopupMessage - Modal overlay for confirmations with optional inputs
 *
 * Core component: No internal dependencies.
 *
 * Variants:
 * - Basic: Message + Decline/Proceed buttons
 * - Text: Adds text input (optional or required)
 * - Image: Adds image upload (optional or required)
 * - Both: Text input + Image upload
 */

import { useState, useRef, useEffect } from 'react';
import { X, Upload, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PopupMessageData {
  text?: string;
  image?: File | null;
}

export interface PopupMessageProps {
  /** Whether the popup is visible */
  isOpen: boolean;
  /** Close handler (called on overlay click or X button) */
  onClose?: () => void;
  /** Main message text */
  message: string;
  /** Optional title */
  title?: string;
  /** Decline button handler */
  onDecline: () => void;
  /** Proceed button handler - receives text/image data */
  onProceed: (data: PopupMessageData) => void;
  /** Decline button label */
  declineLabel?: string;
  /** Proceed button label */
  proceedLabel?: string;
  /** Proceed button color variant */
  proceedVariant?: 'primary' | 'success' | 'danger' | 'warning';
  /** Show text input */
  showTextInput?: boolean;
  /** Text input placeholder */
  textPlaceholder?: string;
  /** Minimum text length required (0 = no minimum) */
  minTextLength?: number;
  /** Text is required to proceed (grays out button until filled) */
  textRequired?: boolean;
  /** Show image upload */
  showImageUpload?: boolean;
  /** Image upload button label */
  imageLabel?: string;
  /** Image is required to proceed (grays out button until uploaded) */
  imageRequired?: boolean;
  /** Loading state on proceed button */
  isLoading?: boolean;
  /** Disable closing on overlay click */
  disableOverlayClose?: boolean;
  /** Custom content to render instead of default inputs */
  customContent?: React.ReactNode;
  /** Disable proceed button externally (useful with customContent) */
  proceedDisabled?: boolean;
}

export function PopupMessage({
  isOpen,
  onClose,
  message,
  title,
  onDecline,
  onProceed,
  declineLabel = 'Cancel',
  proceedLabel = 'Proceed',
  proceedVariant = 'primary',
  showTextInput = false,
  textPlaceholder = 'Enter text...',
  minTextLength = 0,
  textRequired = false,
  showImageUpload = false,
  imageLabel = 'Upload Image',
  imageRequired = false,
  isLoading = false,
  disableOverlayClose = false,
  customContent,
  proceedDisabled = false,
}: PopupMessageProps) {
  const [text, setText] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset state when popup opens/closes
  useEffect(() => {
    if (!isOpen) {
      setText('');
      setImage(null);
    }
  }, [isOpen]);

  // Check if proceed button should be disabled
  const isProceedDisabled = () => {
    if (isLoading) return true;

    // If custom content is provided, use external proceedDisabled prop
    if (customContent) return proceedDisabled;

    // Check text requirement
    if (showTextInput && textRequired) {
      if (minTextLength > 0 && text.length < minTextLength) return true;
      if (text.trim().length === 0) return true;
    }

    // Check image requirement
    if (showImageUpload && imageRequired && !image) return true;

    return false;
  };

  const handleProceed = () => {
    if (isProceedDisabled()) return;
    onProceed({
      text: showTextInput ? text : undefined,
      image: showImageUpload ? image : undefined,
    });
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !disableOverlayClose && onClose) {
      onClose();
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const getProceedButtonStyles = () => {
    const baseStyles = 'flex-1 px-4 py-3 rounded-full text-sm font-semibold transition-all';
    const disabledStyles = 'opacity-50 cursor-not-allowed';

    if (isProceedDisabled()) {
      return cn(baseStyles, 'bg-slate-300 text-slate-500', disabledStyles);
    }

    switch (proceedVariant) {
      case 'success':
        return cn(baseStyles, 'bg-emerald-500 text-white hover:bg-emerald-600 active:bg-emerald-700');
      case 'danger':
        return cn(baseStyles, 'bg-rose-500 text-white hover:bg-rose-600 active:bg-rose-700');
      case 'warning':
        return cn(baseStyles, 'bg-amber-500 text-black hover:bg-amber-600 active:bg-amber-700');
      case 'primary':
      default:
        return cn(baseStyles, 'bg-sky-500 text-white hover:bg-sky-600 active:bg-sky-700');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={handleOverlayClick}
    >
      <div
        className={cn(
          'w-full max-w-sm bg-white rounded-2xl',
          'shadow-[0_8px_32px_rgba(0,0,0,0.12),0_4px_16px_rgba(0,0,0,0.08)]',
          'animate-in fade-in zoom-in-95 duration-200'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-4 pb-2">
          <div className="flex-1">
            {title && (
              <h3 className="text-lg font-bold text-slate-800">{title}</h3>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className={cn(
                'p-1.5 rounded-full',
                'text-slate-400 hover:text-slate-600',
                'hover:bg-slate-100 transition-colors'
              )}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="px-4 pb-4">
          {/* Message */}
          <p className="text-sm text-slate-600 leading-relaxed">{message}</p>

          {/* Custom Content (if provided, replaces default inputs) */}
          {customContent}

          {/* Text Input (only if no customContent) */}
          {!customContent && showTextInput && (
            <div className="mt-4">
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={textPlaceholder}
                rows={3}
                className={cn(
                  'w-full px-4 py-3 rounded-xl',
                  'bg-slate-50 border border-slate-200',
                  'text-sm text-slate-700 placeholder:text-slate-400',
                  'focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent',
                  'resize-none transition-all'
                )}
              />
              {minTextLength > 0 && textRequired && (
                <p className={cn(
                  'mt-1 text-xs',
                  text.length >= minTextLength ? 'text-emerald-600' : 'text-slate-400'
                )}>
                  {text.length}/{minTextLength} characters
                  {text.length >= minTextLength && <Check className="inline w-3 h-3 ml-1" />}
                </p>
              )}
            </div>
          )}

          {/* Image Upload (only if no customContent) */}
          {!customContent && showImageUpload && (
            <div className="mt-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
              {image ? (
                <div
                  className={cn(
                    'w-full flex items-center gap-2',
                    'px-4 py-3 rounded-xl',
                    'border-2 border-emerald-300 bg-emerald-50'
                  )}
                >
                  <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-emerald-600 truncate flex-1">
                    {image.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => setImage(null)}
                    className={cn(
                      'p-1 rounded-full flex-shrink-0',
                      'text-emerald-500 hover:text-emerald-700',
                      'hover:bg-emerald-100 transition-colors'
                    )}
                    aria-label="Remove image"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={triggerFileSelect}
                  className={cn(
                    'w-full flex items-center justify-center gap-2',
                    'px-4 py-3 rounded-xl',
                    'border-2 border-dashed',
                    'border-slate-300 bg-slate-50 text-slate-500',
                    'hover:border-slate-400 hover:bg-slate-100',
                    'text-sm font-medium transition-all'
                  )}
                >
                  <Upload className="w-4 h-4" />
                  {imageLabel}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-3 p-4 pt-2">
          {/* Decline - always on left */}
          <button
            type="button"
            onClick={onDecline}
            disabled={isLoading}
            className={cn(
              'flex-1 px-4 py-3 rounded-full',
              'bg-slate-100 text-slate-700',
              'text-sm font-semibold',
              'hover:bg-slate-200 active:bg-slate-300',
              'transition-all',
              isLoading && 'opacity-50 cursor-not-allowed'
            )}
          >
            {declineLabel}
          </button>

          {/* Proceed - always on right */}
          <button
            type="button"
            onClick={handleProceed}
            disabled={isProceedDisabled()}
            className={getProceedButtonStyles()}
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin mx-auto" />
            ) : (
              proceedLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
